import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { getReservationIdFromCookies } from "./cookieUtils";
import { isLexus } from "../../../common-deprecated/settings/utils/commonSettingUtils";
import Debug from "../../../common-deprecated/Debug";
import type { SaveType } from "../types/SaveTypes";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import { removeSpaces } from "../../../common-deprecated/utils";
import { postalPublish } from "../../../common-deprecated/utils/postalUtils";
import type { BasketType } from "../types/BasketType";
import { isOnlinePurchaseAvailable } from "./basketUtils";
import { POSTAL_CHANNEL_T1_CARCONFIG, POSTAL_TOPIC_CARCONFIG_BUY } from "../../../common-deprecated/constants";
import { ALLOWEDCASHPRODUCT, ALLOWEDMONTHLYPRODUCT } from "./constants/CheckoutConstants";

export const startCheckoutFlow = (save: SaveType, settings: CommonSettingsType, reservation?: boolean): void => {
    if (save.data) {
        // Dispatch event to open the myToyota window
        const reservationId = getReservationIdFromCookies();

        // Specific request from Amaze to also have the saved configuration as the BUY_CAR event payload
        // Don't want to take any chances with whatever logic is behind parsing a potential payload on this event for Toyota, so split it up like this
        if (isLexus(settings)) {
            const body = reservation ? { ...save.data, type: "reserve" } : { ...save.data, reservationId };
            postalPublish({
                channel: POSTAL_CHANNEL_T1_CARCONFIG,
                topic: POSTAL_TOPIC_CARCONFIG_BUY,
                data: body,
            });
        } else {
            const body = reservation ? { type: "reserve" } : { reservationId };
            postalPublish({
                channel: POSTAL_CHANNEL_T1_CARCONFIG,
                topic: POSTAL_TOPIC_CARCONFIG_BUY,
                data: body,
            });
        }
    } else {
        Debug.error("Failed to start buy action: no save data");
    }
};

export const isCheckoutPossible = (save: SaveType): boolean => {
    const configurationCode = save.data?.ConfigurationCode;
    return !save.saving && !!configurationCode;
};
export const containsProductName = (allowedProductName: string, productName: string): boolean => {
    return removeSpaces(allowedProductName).split(",").includes(removeSpaces(productName));
};
export const preventMonthlyCheckoutStepNavigation = (
    allowedCheckoutFinanceProduct: string,
    financeInfoProductName: string,
    financeInfoProductType: string,
    financeOption: FinanceOptionType,
): boolean => {
    // If the allowedCheckoutFinanceProduct contains "cash" and the financeInfoProductName cannot be found and the finance option is set to monthly
    return (
        !!allowedCheckoutFinanceProduct &&
        !containsProductName(allowedCheckoutFinanceProduct, financeInfoProductName) &&
        !containsProductName(allowedCheckoutFinanceProduct, financeInfoProductType) &&
        !containsProductName(allowedCheckoutFinanceProduct, ALLOWEDMONTHLYPRODUCT) &&
        financeOption === "monthly"
    );
};

export const preventCashCheckoutStepNavigation = (
    allowedCheckoutFinanceProduct: string,
    financeInfoProductName: string,
    financeInfoProductType: string,
    financeOption: FinanceOptionType,
): boolean => {
    // If the allowedCheckoutFinanceProduct doesn't contain "cash", but does contain the financeProductInfoName but the finance option is set to cash
    return (
        // Cash is not part of the allowedCheckoutFinanceProduct
        !containsProductName(allowedCheckoutFinanceProduct, ALLOWEDCASHPRODUCT) && financeOption === "cash"
    );
};

export const preventCheckoutStepNavigation = (
    allowedCheckoutFinanceProduct: string,
    financeInfoProductName: string,
    financeInfoProductType: string,
    financeOption: FinanceOptionType,
): boolean => {
    return (
        !!allowedCheckoutFinanceProduct &&
        (preventMonthlyCheckoutStepNavigation(
            allowedCheckoutFinanceProduct,
            financeInfoProductName,
            financeInfoProductType,
            financeOption,
        ) ||
            preventCashCheckoutStepNavigation(
                allowedCheckoutFinanceProduct,
                financeInfoProductName,
                financeInfoProductType,
                financeOption,
            ))
    );
};

export const canNavigateToBuyStep = <T extends { info: { productName?: string; productType?: string } }>(
    enableBuyStep: boolean,
    allowedCheckoutFinanceProduct: string,
    finance: T,
    financeOption: FinanceOptionType,
    basket: BasketType,
): boolean => {
    return (
        enableBuyStep &&
        !preventCheckoutStepNavigation(
            allowedCheckoutFinanceProduct,
            finance.info.productName || "",
            finance.info.productType || "",
            financeOption,
        ) &&
        isOnlinePurchaseAvailable(basket)
    );
};

export const canNavigateToReserveStep = <T extends { info: { productName?: string; productType?: string } }>(
    enableReserveStep: boolean,
    allowedCheckoutReserveProduct: string,
    finance: T,
    financeOption: FinanceOptionType,
): boolean => {
    return (
        enableReserveStep &&
        !preventCheckoutStepNavigation(
            allowedCheckoutReserveProduct,
            finance.info.productName || "",
            finance.info.productType || "",
            financeOption,
        )
    );
};

export const canNavigateToLeaseStep = <T extends { info: { productName?: string; productType?: string } }>(
    enableLeaseStep: boolean,
    allowedCheckoutLeasingProduct: string,
    finance: T,
    financeOption: FinanceOptionType,
): boolean => {
    return (
        enableLeaseStep &&
        !preventCheckoutStepNavigation(
            allowedCheckoutLeasingProduct,
            finance.info.productName || "",
            finance.info.productType || "",
            financeOption,
        )
    );
};
